import Emitter from "@homer.core/emitter";
import clientState from "@homer.core/client.state";

const suffixState = "[state]";
const suffixValue = "[value]";
const instances = new Map();
const classes = document.documentElement.classList;

const getInstance = (storage, identifier) => {
    return (instances.get(storage) || instances.set(storage, {}).get(storage))[identifier];
};

class Storage extends Emitter {
    #private;

    constructor(identifier, storage, { version = undefined, lifetime = null, sticky = true }) {
        const instance = getInstance(storage, identifier);

        if (instance) {
            return instance;
        }

        super();

        this.#private = {
            identifier,
            storage,
            version,
            lifetime,
            sticky,
            prefix: `[${identifier}]`,
            state: undefined,
        };

        this.#private.state = this.get();

        globalThis.addEventListener("storage", (event) => {
            /**
             * Note to devs:
             * Storage-events will only get fired on tabs/windows that did NOT
             * issue the change.
             * If the same value is written as currently is stored in "storage"
             * this will not trigger another storage-event.
             */
            if (!event.storageArea || event.storageArea !== storage) {
                return;
            }

            if (!event.key || !event.key.startsWith(`[${identifier}]`)) {
                return;
            }

            // if (event.key.endsWith(suffixState)) {
            //     const [storageVersion = undefined] = (event.newValue || "").split(",");

            //     if (version && storageVersion && storageVersion !== version) {
            //         /**
            //          * @TODO
            //          * What exactly do we do here?
            //          * 1. fire an event on globalThis which might trigger a dialog to ask the user for a reload?
            //          * 2. eventually mark this storage as "dirty" and prevent future updates of its value until a reload?
            //          */
            //         console.log("### needs update ###", version, storageVersion);
            //     }
            // }

            if (event.key.endsWith(suffixValue)) {
                // see 2. above
                this.#private.state = event.newValue;

                this.emit("change");
            }
        });

        if (sticky) {
            clientState.on("leave", () => {
                this.get();
            });
        }

        instances.get(storage)[identifier] = this;
    }

    get() {
        const { storage, version, prefix, state, sticky } = this.#private;

        try {
            const storageState = storage.getItem(prefix + suffixState);
            const storageValue = storage.getItem(prefix + suffixValue);
            let value;

            if (storageState !== null && storageValue !== null) {
                const now = Date.now();
                const [cacheVersion, cacheExpiration, lastAccess] = (storageState || "").split(",");

                try {
                    if ((cacheVersion || undefined) === version && parseInt(cacheExpiration, 10) > now) {
                        value = storageValue;

                        if (value !== "undefined") {
                            storage.setItem(prefix + suffixState, [cacheVersion, cacheExpiration, now].join(","));
                        }
                    } else {
                        value = "undefined";

                        storage.setItem(prefix + suffixValue, value);
                    }
                } catch (error) {
                    console.error(error);
                }
            }

            if (sticky && typeof value === "undefined" && state) {
                value = state;

                this.set(value);
            }

            this.emit(value === state ? "hit" : "miss");

            return value === "undefined" ? undefined : value;
        } catch (error) {
            console.error(error);
        }

        this.emit("miss");
    }

    set(value) {
        if (classes.contains("isResetting")) {
            return;
        }

        const { storage, version, lifetime, prefix, state } = this.#private;

        try {
            const now = Date.now();

            storage.setItem(
                prefix + suffixState,
                [version, lifetime ? now + lifetime * 1000 : 9007199254740991, now].join(","),
            );

            if (value !== state || storage.getItem(prefix + suffixValue) === null) {
                storage.setItem(prefix + suffixValue, value);
            }

            if (value !== state) {
                this.#private.state = value;

                this.emit("change");
            }
        } catch (error) {
            console.error(error);
        }

        return this;
    }

    remove() {
        const { prefix, storage, sticky } = this.#private;

        try {
            switch (sticky) {
                case true:
                    storage.setItem(prefix + suffixValue, "undefined");

                    break;
                default:
                    storage.removeItem(prefix + suffixState);
                    storage.removeItem(prefix + suffixValue);

                    break;
            }

            this.#private.state = undefined;

            this.emit("remove");
        } catch (error) {
            console.error(error);
        }

        return this;
    }
}

export default Storage;
